/**
 * AssetList — split view: Computers (NinjaOne/Intune) and Mobile Devices (SOTI).
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FleetAnalytics } from './FleetAnalytics';
import type { HardwareAsset } from '@/types';

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString();
}

type DeviceCategory = 'computers' | 'mobile' | 'peripherals';
type ViewFilter = 'all' | 'online' | 'offline' | 'noncompliant' | 'inventory';

interface ApiResult {
  data: HardwareAsset[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface Stats {
  assets: { total: number; inUse: number; inventory: number; laptops: number; desktops: number; ninjaOnline: number; ninjaOffline: number; intuneCompliant: number; intuneNonCompliant: number };
  mobile: { total: number; online: number; offline: number };
  peripherals: { total: number };
}

export function AssetList() {
  const [searchParams] = useSearchParams();
  const initialFilter = searchParams.get('filter') as ViewFilter | null;
  const [mainView, setMainView] = useState<'inventory' | 'fleet'>('inventory');
  const [category, setCategory] = useState<DeviceCategory>('computers');
  const [view, setView] = useState<ViewFilter>(initialFilter && ['all','online','offline','noncompliant','inventory'].includes(initialFilter) ? initialFilter : 'all');
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<ApiResult | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/dashboard', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.assets) setStats({ assets: d.assets, mobile: d.mobile || { total: 0, online: 0, offline: 0 }, peripherals: d.peripherals || { total: 0 } }); })
      .catch(() => {});
  }, []);

  const loadAssets = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('page', String(page));
      qs.set('pageSize', '25');
      qs.set('sortBy', 'deviceName');
      qs.set('sortDir', 'asc');

      if (activeSearch) qs.set('search', activeSearch);
      if (siteFilter) qs.set('siteNumber', siteFilter);

      // Pass category to API for server-side filtering
      qs.set('category', category);

      switch (view) {
        case 'inventory': qs.set('lifecycleStatus', 'Inventory'); break;
        case 'online': qs.set('ninjaOnline', 'true'); break;
        case 'offline': qs.set('ninjaOnline', 'false'); break;
        case 'noncompliant': qs.set('intuneCompliance', 'noncompliant'); break;
      }

      const resp = await fetch(`/api/assets?${qs.toString()}`, { credentials: 'include' });
      if (resp.ok) setResult(await resp.json());
    } catch { /* */ }
    setLoading(false);
  }, [category, view, activeSearch, siteFilter, page]);

  useEffect(() => { loadAssets(); }, [loadAssets]);

  const doSearch = () => { setActiveSearch(searchInput.trim()); setPage(1); };
  const clearSearch = () => { setSearchInput(''); setActiveSearch(''); setPage(1); };
  const switchView = (v: ViewFilter) => { setView(v); setPage(1); };
  const switchCategory = (c: DeviceCategory) => { setCategory(c); setView('all'); setActiveSearch(''); setSearchInput(''); setPage(1); };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-lg font-semibold text-text-primary">Assets</h1>
      </div>

      {/* Main View Tabs */}
      <div className="flex gap-1 mb-5 border-b border-border">
        <button
          onClick={() => { setMainView('inventory'); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-all ${mainView === 'inventory' ? 'border-baker-red text-baker-red' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Inventory
        </button>
        <button
          onClick={() => { setMainView('fleet'); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-all ${mainView === 'fleet' ? 'border-baker-red text-baker-red' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Fleet & Lifecycle
        </button>
      </div>

      {mainView === 'fleet' ? (
        <FleetAnalytics />
      ) : (
      <>

      {/* Category Tabs */}
      <div className="flex gap-1 mb-5 border-b border-border">
        <button
          onClick={() => switchCategory('computers')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-all ${category === 'computers' ? 'border-baker-red text-baker-red' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          💻 Computers {stats ? `(${stats.assets.total.toLocaleString()})` : ''}
        </button>
        <button
          onClick={() => switchCategory('mobile')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-all ${category === 'mobile' ? 'border-baker-red text-baker-red' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          📱 Mobile Devices {stats ? `(${stats.mobile.total.toLocaleString()})` : ''}
        </button>
        <button
          onClick={() => switchCategory('peripherals')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-all ${category === 'peripherals' ? 'border-baker-red text-baker-red' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          🔌 Peripherals {stats ? `(${stats.peripherals.total.toLocaleString()})` : ''}
        </button>
      </div>

      {/* KPI Tiles */}
      {stats && category === 'computers' && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-5">
          <Tile label="Total" value={stats.assets.total} active={view === 'all'} onClick={() => switchView('all')} />
          <Tile label="Laptops" value={stats.assets.laptops} />
          <Tile label="Desktops" value={stats.assets.desktops} />
          <Tile label="Online" value={stats.assets.ninjaOnline} active={view === 'online'} onClick={() => switchView('online')} color="green" />
          <Tile label="Offline" value={stats.assets.ninjaOffline} active={view === 'offline'} onClick={() => switchView('offline')} color="red" />
          <Tile label="Non-Compliant" value={stats.assets.intuneNonCompliant} active={view === 'noncompliant'} onClick={() => switchView('noncompliant')} color="red" />
        </div>
      )}
      {stats && category === 'mobile' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <Tile label="Total" value={stats.mobile.total} active={view === 'all'} onClick={() => switchView('all')} />
        </div>
      )}
      {stats && category === 'peripherals' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <Tile label="Total" value={stats.peripherals.total} active={view === 'all'} onClick={() => switchView('all')} />
        </div>
      )}

      {/* Search */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <input
          type="text"
          placeholder={category === 'computers' ? 'Search by name, serial, user...' : 'Search by name, serial, site...'}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') doSearch(); }}
          className="py-2 px-3 rounded-lg border border-border text-sm min-w-[300px] focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
        />
        <button onClick={doSearch} className="px-4 py-2 rounded-lg bg-baker-red text-white text-sm font-medium hover:bg-baker-red/90">Search</button>
        {activeSearch && <button onClick={clearSearch} className="px-4 py-2 rounded-lg border border-border text-sm">Clear</button>}
        <input
          type="text"
          placeholder="Site #"
          value={siteFilter}
          onChange={(e) => { setSiteFilter(e.target.value); setPage(1); }}
          className="py-2 px-3 rounded-lg border border-border text-sm w-20"
        />
      </div>

      {view !== 'all' && (
        <div className="mb-3 flex items-center gap-2">
          <span className="text-xs px-2 py-1 rounded-full bg-baker-red/10 text-baker-red font-medium">
            Filter: {view === 'inventory' ? 'Inventory' : view === 'online' ? 'Online' : view === 'offline' ? 'Offline' : 'Non-Compliant'}
          </span>
          <button onClick={() => switchView('all')} className="text-xs text-slate-500 hover:text-baker-red">× Clear</button>
        </div>
      )}

      {/* Table */}
      {loading ? <p className="text-slate-500">Loading...</p> : result && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b-2 border-border">
                  <Th>Device Name</Th>
                  <Th>Serial</Th>
                  {category === 'computers' && <Th>Type</Th>}
                  <Th>Model</Th>
                  <Th>Site</Th>
                  <Th>User</Th>
                  <Th>Status</Th>
                  {category === 'computers' && <Th>Ninja</Th>}
                  {category === 'mobile' && <Th>Battery</Th>}
                  <Th>Last Seen</Th>
                </tr>
              </thead>
              <tbody>
                {result.data.map((a) => {
                  const ext = a as unknown as Record<string, unknown>;
                  const ninjaOnline = ext.ninjaOnline ?? ext.NinjaOnline;
                  const sotiBattery = ext.sotiBatteryLevel ?? ext.SotiBatteryLevel;
                  const ninjaLast = ext.ninjaLastContact ?? ext.NinjaLastContact;
                  const intuneLast = ext.intuneLastSync ?? ext.IntuneLastSync;
                  const sotiLast = ext.sotiLastCheckIn ?? ext.SotiLastCheckIn;
                  const lastSeen = category === 'mobile' ? sotiLast : (ninjaLast ?? intuneLast);
                  const isOnline = category === 'mobile' ? null : ninjaOnline;

                  return (
                    <tr key={a.assetId} className="border-b border-border-subtle hover:bg-page/50">
                      <td className="py-2 px-2">
                        <Link to={`/assets/${a.assetId}`} className="text-baker-red no-underline hover:underline font-medium">
                          {a.deviceName ?? '—'}
                        </Link>
                      </td>
                      <td className="py-2 px-2 font-mono text-xs">{a.serialNumber}</td>
                      {category === 'computers' && <td className="py-2 px-2">{a.deviceType ?? '—'}</td>}
                      <td className="py-2 px-2 text-slate-600">{a.model ?? '—'}</td>
                      <td className="py-2 px-2">
                        {a.siteNumber ? <Link to={`/sites/${a.siteNumber}`} className="text-baker-red no-underline hover:underline">{a.siteNumber}</Link> : '—'}
                      </td>
                      <td className="py-2 px-2">{a.assignedUser ?? '—'}</td>
                      <td className="py-2 px-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          a.lifecycleStatus === 'In Use' ? 'bg-emerald-100 text-emerald-700' :
                          a.lifecycleStatus === 'Inventory' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>{a.lifecycleStatus}</span>
                      </td>
                      {category === 'computers' && (
                        <td className="py-2 px-2">
                          {isOnline != null ? (
                            <span className={`inline-block w-2.5 h-2.5 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-red-500'}`} title={isOnline ? 'Online' : 'Offline'} />
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                      )}
                      {category === 'mobile' && (
                        <td className="py-2 px-2 text-xs">
                          {sotiBattery != null ? `${sotiBattery}%` : '—'}
                        </td>
                      )}
                      <td className="py-2 px-2 text-slate-500 text-xs">{lastSeen ? formatDate(String(lastSeen)) : '—'}</td>
                    </tr>
                  );
                })}
                {result.data.length === 0 && (
                  <tr><td colSpan={category === 'mobile' ? 9 : 9} className="py-8 text-center text-slate-400">No assets found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {result.totalPages > 1 && (
            <div className="flex justify-center items-center gap-3 mt-4">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 rounded border border-border text-sm disabled:opacity-40">Prev</button>
              <span className="text-sm text-slate-600">Page {result.page} of {result.totalPages} ({result.total} total)</span>
              <button disabled={page >= result.totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 rounded border border-border text-sm disabled:opacity-40">Next</button>
            </div>
          )}
        </>
      )}
      </>
      )}
    </div>
  );
}

function Tile({ label, value, active, onClick, color }: { label: string; value: number; active?: boolean; onClick?: () => void; color?: string }) {
  const colorClass = color === 'green' ? 'text-emerald-600' : color === 'red' ? 'text-red-600' : color === 'blue' ? 'text-blue-600' : 'text-text-primary';
  return (
    <button
      onClick={onClick}
      className={`bg-white rounded-lg border p-3 text-left transition-all ${onClick ? 'cursor-pointer' : 'cursor-default'} ${
        active ? 'border-baker-accent shadow-sm' : 'border-border hover:border-baker-accent/40'
      }`}
    >
      <div className={`text-xl font-bold ${colorClass}`}>{value.toLocaleString()}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </button>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-left py-2 px-2 font-medium text-slate-600 text-xs uppercase tracking-wide">{children}</th>;
}
